import { Component, effect, signal, computed, ViewChild } from '@angular/core';
import { DefinitionService } from '../../core/services/definition/definition.service';
import { finalize } from 'rxjs';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { DefinitionType } from '../../core/enums/definition-type';
import { CreateDefinitionValueDto, DefinitionModelDto } from '../../core/models/definitions/definition-model-dto';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card'
import { FormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ManufacturerModelsDialogComponent } from './components/manufacturer-models-dialog/manufacturer-models-dialog/manufacturer-models-dialog.component';
import { MachineManufacturerModelsDialogInfoModel } from './models/machine-manufacturer-models-dialog-info.model';
import { MachineManufacturerModelDto } from '../../core/models/definitions/machine-manufacturer-model-dto';
import { AuthService } from '../../core/services/auth.service';
import { AuthPolicy } from '../../core/enums/auth-policy';
export const DEFINITION_TYPE_LABELS: Record<DefinitionType, string> = {
  [DefinitionType.HarvesterHead]: 'Aggregatsfabrikat',
  [DefinitionType.MachineComputer]: 'Maskindatorer',
  [DefinitionType.MachineManufacturer]: 'Tillverkare',
};
@Component({
  selector: 'app-definition-view',
  imports: [MatFormFieldModule, MatSelectModule, MatButtonModule, MatPaginatorModule, MatSortModule,
    MatProgressSpinnerModule, MatCardModule, FormsModule, MatTableModule, MatInputModule,
    MatOptionModule, MatSelectModule, MatDialogModule],
  templateUrl: './definition-view.component.html',
  styleUrl: './definition-view.component.scss'
})

export class DefinitionViewComponent {
  definitionTypes = Object.values(DefinitionType).filter(key => !isNaN(Number(key))) as DefinitionType[];
  manufacturerTypeValue = DefinitionType.MachineManufacturer;

  displayedColumns: string[] = ['name'];

  selectedType = signal<DefinitionType>(DefinitionType.HarvesterHead);
  definitionValues = computed<DefinitionModelDto[]>(() => {
    switch (this.selectedType()) {
      case DefinitionType.HarvesterHead:
        return this.definitionService.harvesterHeads();
      case DefinitionType.MachineComputer:
        return this.definitionService.machineComputers();
      case DefinitionType.MachineManufacturer:
        return this.definitionService.machineManufacturers();
      default:
        return [];
    }
  });
  definitionSubValues = signal<MachineManufacturerModelDto[]>([]);
  definitionDataSource = new MatTableDataSource<DefinitionModelDto>();

  loading = computed(() => this.definitionService.isLoading());

  newItem: CreateDefinitionValueDto = { name: '', shortName: '' };

  modifiedRows: Set<number> = new Set();
  isPolicyPTAdmin: boolean = false;

  @ViewChild('definitionPaginator') set paginator(pager: MatPaginator) {
    if (pager) this.definitionDataSource.paginator = pager;
  }
  @ViewChild(MatSort) sort: MatSort | undefined;

  constructor(private definitionService: DefinitionService,
    private dialog: MatDialog,
    public authService: AuthService) {

    this.definitionService.loadAllDefinitions();

    effect(() => {
      const data = this.definitionValues();
      this.definitionDataSource.data = data;
      this.definitionDataSource.sort = this.sort!;
      this.definitionDataSource.sortingDataAccessor = (item: any, property: string) => {
        if (property) return (item as any)[property]?.toLowerCase?.() ?? '';
        return '';
      };
      this.handleShortNameColumn();
    });
  }

  private handleShortNameColumn() {
    const shortNameIndex = this.displayedColumns.indexOf('shortName');
    if (this.selectedType() === this.manufacturerTypeValue) {
      if (shortNameIndex === -1) {
        this.displayedColumns = ['shortName', ...this.displayedColumns];
      }
    } else {
      if (shortNameIndex !== -1) {
        this.displayedColumns.splice(shortNameIndex, 1);
      }
    }
  }

  ngOnInit() {
    if (this.authService.isPolicy(AuthPolicy.PTAdmin)) {
      this.displayedColumns.push('action');
      this.isPolicyPTAdmin = true;
    }
  }

  getDisplayName(type: DefinitionType): string {
    return DEFINITION_TYPE_LABELS[type];
  }

  selectDefinitionType(event: MatSelectChange) {
    this.selectedType.set(event.value);
    this.resetNewItem();
  }

  isValidNewItem(): boolean {
    if (this.selectedType() === this.manufacturerTypeValue) {
      return this.newItem.name.trim() !== '' && this.newItem.shortName.trim() !== '';
    }
    return this.newItem.name.trim() !== '';
  }

  addItem() {
    if (!this.isValidNewItem()) {
      return;
    }

    this.definitionService.createDefinitionValue(this.selectedType(), this.newItem).pipe(
      finalize(() => {
        this.resetNewItem()
      })
    ).subscribe();
  }

  private resetNewItem() {
    this.newItem = { name: '', shortName: '' };
  }

  onInputChange(rowIndex: number): void {
    this.modifiedRows.add(rowIndex);
  }

  isRowModified(rowIndex: number): boolean {
    return this.modifiedRows.has(rowIndex);
  }

  isDefinitionValueUsed(element: DefinitionModelDto) {
    return element.isUsed;
  }

  saveRow(element: DefinitionModelDto, rowIndex: number): void {
    this.definitionService.updateDefinitionValue(this.selectedType(), element);
    console.log(`Saving row ${rowIndex}`);
    this.modifiedRows.delete(rowIndex);
  }

  deleteRow(element: DefinitionModelDto): void {
    const index = this.definitionDataSource.data.findIndex(item => item.id === element.id);
    if (index !== -1) {
      this.definitionDataSource.data.splice(index, 1);
      this.definitionDataSource._updateChangeSubscription();
    }

    this.definitionService.deleteDefinitionValue(this.selectedType(), element).pipe(
    ).subscribe();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.definitionDataSource.filter = filterValue.trim().toLowerCase();
  }

  async showManufacturerModels(element: DefinitionModelDto): Promise<void> {
    const inputData: MachineManufacturerModelsDialogInfoModel = {
      machineManufacturerId: element.id,
      machineManufacturerName: element.name
    };

    this.dialog.closeAll();
    const dialogRef = this.dialog.open(ManufacturerModelsDialogComponent, {
      data: inputData,
      maxWidth: '90vw',
      closeOnNavigation: true,
      autoFocus: false,
      restoreFocus: false
    });
  }
}
